// Supabase JS wraps Edge Function non-2xx responses in a FunctionsHttpError
// whose `.message` is generic and whose `.context` is the underlying Response.
// The response body contains the actionable error contract.

export type EdgeFunctionErrorLike = {
  context?: {text?: () => Promise<string>} | null;
  message?: string;
} | null;

export type EdgeFunctionFailure = {
  message: string;
  code: string | null;
  step: string | null;
  ref: string | null;
  partial: {
    authUserId: string;
    profileCreated: boolean | null;
  } | null;
};

const EDGE_CODE_RE = /^[A-Z][A-Z0-9_]{1,63}$/;
const EDGE_STEP_RE = /^[A-Za-z][A-Za-z0-9_-]{0,39}$/;
const EDGE_REF_RE = /^[A-Za-z0-9_-]{4,32}$/;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;

const safeToken = (value: unknown, pattern: RegExp): string | null => {
  if (typeof value !== 'string') return null;
  return pattern.test(value) ? value : null;
};

const toFailure = (message: string, body?: Record<string, unknown> | null): EdgeFunctionFailure => {
  const partialBody = asRecord(body?.partial);
  const authUserId = safeToken(partialBody?.authUserId, UUID_RE);

  return {
    message,
    code: safeToken(body?.code, EDGE_CODE_RE),
    step: safeToken(body?.step, EDGE_STEP_RE),
    ref: safeToken(body?.ref, EDGE_REF_RE),
    partial: authUserId
      ? {
          authUserId,
          profileCreated: typeof partialBody?.profileCreated === 'boolean' ? partialBody.profileCreated : null,
        }
      : null,
  };
};

// Reads the response exactly once. Metadata is allowlisted and shape-checked so
// callers can branch on it without trusting arbitrary server-controlled text.
export async function readEdgeFunctionFailure(err: EdgeFunctionErrorLike): Promise<EdgeFunctionFailure> {
  const fallbackMessage = err && typeof err.message === 'string' && err.message ? err.message : 'Unknown error';

  if (err && err.context && typeof err.context.text === 'function') {
    try {
      const body = await err.context.text();
      if (body) {
        try {
          const parsedBody = asRecord(JSON.parse(body) as unknown);
          const parsedError = parsedBody?.error;
          if (typeof parsedError === 'string' && parsedError) {
            return toFailure(parsedError, parsedBody);
          }
          const parsedMessage = parsedBody?.message;
          if (typeof parsedMessage === 'string' && parsedMessage) {
            return toFailure(parsedMessage, parsedBody);
          }
          return toFailure(fallbackMessage, parsedBody);
        } catch (_jsonErr) {
          return toFailure(body);
        }
      }
    } catch (_readErr) {
      // Body already consumed or read failed - fall through to .message.
    }
  }

  return toFailure(fallbackMessage);
}

// Backward-compatible string-only API used by existing callers.
export async function unwrapEdgeFunctionError(err: EdgeFunctionErrorLike): Promise<string> {
  return (await readEdgeFunctionFailure(err)).message;
}
